import React, { useEffect, useState } from "react";
import { ClipboardCheck, Loader2, Check, CheckCircle2, Users } from "lucide-react";
import api from "../../lib/api";
import { PageTitle, Card, Badge, Loading, fmtDate } from "./ui";

export default function Attendance() {
  const [meetings, setMeetings] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [present, setPresent] = useState({});
  const [save, setSave] = useState({ state: "idle" });

  const loadMeetings = () =>
    api.get("/officer/attendance/meetings").then((r) => {
      setMeetings(r.data);
      if (r.data.length && !activeId) select(r.data[0].id);
    }).catch(() => setMeetings([]));

  useEffect(() => { loadMeetings(); /* eslint-disable-next-line */ }, []);

  const select = async (id) => {
    setActiveId(id);
    setDetail(null);
    setSave({ state: "idle" });
    const { data } = await api.get(`/officer/attendance/${id}`);
    setDetail(data);
    setPresent(Object.fromEntries(data.members.map((m) => [m.email, m.present])));
  };

  const toggle = (email) => setPresent((p) => ({ ...p, [email]: !p[email] }));
  const setAll = (val) =>
    setPresent(Object.fromEntries((detail?.members || []).map((m) => [m.email, val])));

  const commit = async () => {
    setSave({ state: "saving" });
    try {
      const emails = Object.keys(present).filter((e) => present[e]);
      await api.put(`/officer/attendance/${activeId}`, { present_emails: emails });
      setSave({ state: "done" });
      loadMeetings();
    } catch {
      setSave({ state: "error" });
    }
  };

  if (!meetings) return <Loading />;
  const presentCount = Object.values(present).filter(Boolean).length;

  return (
    <div data-testid="attendance-page">
      <PageTitle eyebrow="Secretary" title="Record Attendance"
        sub="Tick who was present at each meeting — the engagement KPIs update from these records." />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Meeting list */}
        <Card className="lg:col-span-1">
          <h3 className="mb-4 font-display text-lg font-semibold text-sapphire-deep">Meetings</h3>
          <div className="space-y-2" data-testid="attendance-meeting-list">
            {meetings.map((m) => (
              <button
                key={m.id}
                onClick={() => select(m.id)}
                className={`w-full rounded-xl border p-3 text-left transition ${
                  activeId === m.id ? "border-gold bg-gold/10" : "border-sapphire-deep/10 hover:border-gold/50"
                }`}
                data-testid={`attendance-meeting-${m.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-base text-sapphire-deep">{m.title}</span>
                  {m.recorded && <Check size={15} className="text-emerald" />}
                </div>
                <div className="mt-1 flex items-center justify-between font-sans text-xs text-ink/55">
                  <span>{fmtDate(m.date)}</span>
                  <span>{m.recorded ? `${m.present_count}/${m.total_members} present` : "Not recorded"}</span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Roster */}
        <Card className="lg:col-span-2">
          {!detail ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold-deep" /></div>
          ) : (
            <>
              <div className="flex flex-col gap-3 border-b border-sapphire-deep/10 pb-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="font-display text-xl font-semibold text-sapphire-deep">{detail.event.title}</h3>
                  <p className="font-sans text-sm text-ink/55">{fmtDate(detail.event.date)} · <Users size={13} className="inline" /> {presentCount} present</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setAll(true)} className="rounded-full border border-sapphire-deep/15 px-3 py-1.5 font-sans text-xs font-semibold uppercase tracking-widest text-ink/65 hover:border-gold hover:text-gold-deep">All</button>
                  <button onClick={() => setAll(false)} className="rounded-full border border-sapphire-deep/15 px-3 py-1.5 font-sans text-xs font-semibold uppercase tracking-widest text-ink/65 hover:border-gold hover:text-gold-deep">None</button>
                </div>
              </div>

              <div className="mt-4 divide-y divide-sapphire-deep/8">
                {detail.members.map((m) => (
                  <label key={m.email} className="flex cursor-pointer items-center justify-between py-3" data-testid={`attendance-row-${m.email}`}>
                    <div>
                      <p className="font-display text-lg text-sapphire-deep">{m.name}</p>
                      <p className="font-sans text-xs text-gold-deep">{m.rank}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!present[m.email]}
                      onChange={() => toggle(m.email)}
                      className="h-5 w-5 accent-emerald"
                      data-testid={`attendance-check-${m.email}`}
                    />
                  </label>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-4">
                <button
                  onClick={commit}
                  disabled={save.state === "saving"}
                  className="flex items-center gap-2 rounded-full bg-sapphire-deep px-6 py-3 font-sans text-sm font-semibold uppercase tracking-widest text-parchment transition hover:bg-sapphire-mid disabled:opacity-40"
                  data-testid="attendance-save-btn"
                >
                  {save.state === "saving" ? <Loader2 size={15} className="animate-spin" /> : <ClipboardCheck size={15} />}
                  {save.state === "saving" ? "Saving…" : "Save Attendance"}
                </button>
                {save.state === "done" && (
                  <span className="flex items-center gap-2 font-sans text-sm text-emerald" data-testid="attendance-saved">
                    <CheckCircle2 size={16} /> Saved — KPIs updated.
                  </span>
                )}
                {save.state === "error" && <span className="font-sans text-sm text-garnet">Could not save. Try again.</span>}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
